// frontend/app/components/lessonPlans/authoring/coreBlockSchemas.js
//
// The thirteen block types Lesson in a Box shipped with: content, Saras
// assets, and graded checks. Split out of blockSchemas.js so that file stays
// the registry — schemaFor, makeBlock, validateBlock — rather than the table.
//
// `phase` here is the Loop step a type is usually reached for; a graded check
// does not declare `strictPhase`, because a quick MCQ in Understand is a
// teacher's call rather than a mistake.

import {
  assetTierField,
  conceptField,
  EVIDENCE_WEIGHT,
  explanationField,
  FIELD,
  instructionsField,
  placeholderField,
  titleField,
} from "./blockFields";

export const CORE_BLOCK_SCHEMAS = [
  // ---- content -----------------------------------------------------------
  {
    type: "rich_text",
    label: "Paragraph",
    icon: "text",
    group: "content",
    fields: [
      titleField,
      {
        key: "text",
        label: "Text",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Start with a fact or story that hooks students…",
      },
    ],
  },
  {
    type: "bullet_list",
    label: "Bullet list",
    icon: "format-list-bulleted",
    group: "content",
    fields: [
      titleField,
      {
        key: "items",
        label: "Points",
        kind: FIELD.STRING_LIST,
        required: true,
        placeholder: "State the key idea in one clear sentence.",
        addLabel: "Add point",
      },
    ],
  },
  {
    type: "callout",
    label: "Callout",
    icon: "information-outline",
    group: "content",
    fields: [
      {
        key: "variant",
        label: "Style",
        kind: FIELD.CHIPS,
        defaultValue: "info",
        options: [
          { value: "question", label: "Hook question", accent: "#6366f1" },
          { value: "info", label: "Information", accent: "#3b82f6" },
          { value: "warning", label: "Watch out", accent: "#f59e0b" },
          { value: "success", label: "Key point", accent: "#10b981" },
        ],
      },
      titleField,
      {
        key: "text",
        label: "Text",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "What question do you want students to think about?",
      },
    ],
  },
  {
    type: "teacher_tip",
    label: "Teacher tip",
    icon: "lightbulb-on-outline",
    group: "content",
    // Stripped server-side for students; the badge in the palette says so.
    teacherOnly: true,
    fields: [
      {
        key: "text",
        label: "Tip",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Collect predictions on the board before revealing anything.",
      },
    ],
  },
  {
    type: "ai_prompt",
    label: "Ask Saras card",
    icon: "robot-happy-outline",
    group: "content",
    fields: [
      { ...titleField, placeholder: "Investigate with Saras AI" },
      {
        key: "questions",
        label: "Suggested questions",
        kind: FIELD.STRING_LIST,
        required: true,
        placeholder: "What might students ask about this topic?",
        addLabel: "Add question",
        hint: "These also become the suggestions in the Saras rail for this step.",
      },
    ],
  },

  // ---- Saras assets ------------------------------------------------------
  {
    type: "saras_pwo",
    label: "3D scene",
    icon: "cube-outline",
    group: "asset",
    fields: [
      { ...titleField, label: "Heading", placeholder: "Overview of the model" },
      instructionsField,
      { key: "asset_ref", label: "Scene", kind: FIELD.PWO_REF, required: true },
      assetTierField,
    ],
  },
  {
    type: "saras_video",
    label: "Video segment",
    icon: "play-circle-outline",
    group: "asset",
    fields: [
      { ...titleField, placeholder: "Introduction to the topic" },
      instructionsField,
      { key: "asset_ref", label: "Video tag", kind: FIELD.VIDEO_REF, required: true },
      assetTierField,
    ],
  },
  {
    type: "saras_topic_quiz",
    label: "Topic quiz",
    icon: "clipboard-check-outline",
    group: "asset",
    fields: [
      { ...titleField, placeholder: "Topic quiz" },
      instructionsField,
      {
        key: "asset_ref",
        label: "Quiz",
        kind: FIELD.TOPIC_QUIZ_REF,
        hint: "Defaults to this lesson's topic.",
      },
      assetTierField,
    ],
  },

  // ---- checks ------------------------------------------------------------
  {
    type: "multiple_choice",
    label: "Multiple choice",
    icon: "format-list-checks",
    group: "check",
    phase: "apply",
    evidenceWeight: EVIDENCE_WEIGHT.GRADED,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Write the question students will answer.",
      },
      { key: "options", label: "Answers", kind: FIELD.OPTIONS, required: true },
      explanationField,
      conceptField,
    ],
  },
  {
    type: "multi_select",
    label: "Select all that apply",
    icon: "checkbox-multiple-marked-outline",
    group: "check",
    phase: "apply",
    evidenceWeight: EVIDENCE_WEIGHT.GRADED,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Write the question, then mark every correct answer.",
      },
      { key: "options", label: "Answers", kind: FIELD.MULTI_OPTIONS, required: true },
      explanationField,
      conceptField,
    ],
  },
  {
    type: "ordering",
    label: "Put in order",
    icon: "sort",
    group: "check",
    phase: "apply",
    evidenceWeight: EVIDENCE_WEIGHT.GRADED,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Describe what students should put in order.",
      },
      {
        key: "items",
        label: "Steps, in the correct order",
        kind: FIELD.ORDER_ITEMS,
        required: true,
        hint: "Students see them shuffled; the order you set here is the answer.",
      },
      explanationField,
      conceptField,
    ],
  },
  {
    type: "short_answer",
    label: "Short answer",
    icon: "text-short",
    group: "check",
    phase: "apply",
    evidenceWeight: EVIDENCE_WEIGHT.GRADED,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Write the question students will answer.",
      },
      placeholderField,
      conceptField,
    ],
  },
  {
    type: "prediction",
    label: "Prediction",
    icon: "help-rhombus-outline",
    group: "written",
    phase: "wonder",
    // Weight 0 on purpose: a prediction comes before instruction, so scoring
    // it would punish honest guessing.
    evidenceWeight: EVIDENCE_WEIGHT.NONE,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "What do you think will happen?",
      },
      placeholderField,
      {
        key: "ai_feedback",
        label: "Saras responds to the guess",
        kind: FIELD.TOGGLE,
        defaultValue: true,
        hint: "Never right or wrong — it reflects the guess back to them.",
      },
      {
        key: "confidence",
        label: "Ask how sure they are",
        kind: FIELD.TOGGLE,
        defaultValue: false,
        hint: "Captures a 1–5 self-report. Feeds confidence calibration, not the score.",
      },
      conceptField,
    ],
  },
];
